import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * icons-with-content — Shrine's two-column "icons list + free content" hybrid, brought to CONFIG-BY-CONFIG
 * parity. The section pairs a repeatable `icon` list (icon + heading + text) with a free CONTENT column
 * assembled from single-instance blocks (heading / caption / text / button / add-to-cart) and a MEDIA
 * block (image OR autoplay video). Every Shrine setting in the inventory is implemented here and wired in
 * Component.tsx; only the genuine Shopify-platform settings are deferred (see the deferral notes below and
 * the section comment in Component.tsx).
 *
 * THEME MAPPING: COLOR comes from `--aqx-scheme-*` (universal `colorScheme`), type/spacing/radius from
 * `--aqx-theme-*`. The shared "Section" universal group (colorScheme / padding / width) is appended for
 * inspector consistency, and the same shared engines other sections use are reused for the 14 background
 * patterns. Shrine setting ids are preserved 1:1 wherever they don't collide with a platform concept.
 *
 * DEFERRED (Shopify-platform only, each justified):
 *  - color_scheme / icon_color theme COLOR-SCHEME classes → we use our universal scheme + a curated
 *    icon-color select mapped to `--aqx-scheme-*` tokens (no Liquid color-scheme CSS vars exist here).
 *  - custom_gradient_background / custom_image_background / custom_mobile_image_background → the universal
 *    scheme owns the background; gradient/asset-picker overrides are a separate cross-section concern.
 *  - Material-Symbols glyph FONT + `filled_icon` fill axis → rendered as a curated inline emoji glyph OR an
 *    uploaded custom image (SSR-pure, zero webfont). `filled_icon` has no analogue without the font.
 *  - i18n `t:` translation keys, `disabled_on.groups`, `display_id` (Shrine section-merge helper),
 *    animate-* reveal classes + Liquid `shopify_attributes` → platform/rendering coupling, not schema knobs.
 */
export const iconsWithContentSchema = {
    type: 'icons-with-content',
    name: 'Icons with content',
    version: 2,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Display / visibility (Shrine visibility) ───────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            group: 'Section',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        {
            // Section color scheme. `custom` reveals the background/text/button pickers in "Custom colors"
            // (Shrine color_scheme=custom); light/inverse/accent follow the theme `--aqx-scheme-*` bands.
            // Declared section-locally (not from `universalFields`) so it can carry the extra `custom` value.
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Section',
            default: 'light',
            options: [
                { value: 'light', label: 'Background 1' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'accent', label: 'Accent' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        // ── Icons section (Shrine "Icons section" header) ──────────────────────────
        {
            id: 'iconSize',
            type: 'select',
            label: 'Icon size',
            group: 'Icons section',
            default: 's',
            options: [
                { value: 'xs', label: 'XS' },
                { value: 's', label: 'S' },
                { value: 'm', label: 'M' },
                { value: 'l', label: 'L' },
                { value: 'xl', label: 'XL' },
                { value: 'xxl', label: 'XXL' },
            ],
        },
        {
            id: 'iconPosition',
            type: 'select',
            label: 'Icon position',
            group: 'Icons section',
            default: 'next-to-title',
            options: [
                { value: 'above', label: 'Above title & text' },
                { value: 'next-to-title', label: 'Next to title' },
                { value: 'next-to-text', label: 'Next to title & text' },
            ],
        },
        {
            id: 'iconColor',
            type: 'select',
            label: 'Icon color',
            group: 'Icons section',
            default: 'custom',
            // DEFER theme color-scheme classes → map to our scheme tokens; `custom` uses the picker below.
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'iconCustomColor',
            type: 'color',
            label: 'Icon custom color',
            group: 'Icons section',
            default: { ref: 'theme', token: 'ink' },
            visibleWhen: { field: 'iconColor', equals: 'custom' },
        },
        {
            id: 'iconHeadingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Icons section',
            default: 'h3',
            options: [
                { value: 'h5', label: 'Extra small' },
                { value: 'h4', label: 'Small' },
                { value: 'h3', label: 'Medium' },
                { value: 'h2', label: 'Large' },
            ],
        },
        {
            id: 'iconTextAlignment',
            type: 'select',
            label: 'Icon text alignment',
            group: 'Icons section',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'iconsDesktopLayout',
            type: 'select',
            label: 'Desktop icons layout',
            group: 'Icons section',
            default: '1-column',
            info: 'How many columns the icon list uses on desktop: 1 stacks each icon-and-text on its own row; 2 places them two per row.',
            options: [
                { value: '1-column', label: '1 column' },
                { value: '2-columns', label: '2 columns' },
            ],
        },
        {
            id: 'iconsMobileLayout',
            type: 'select',
            label: 'Mobile icons layout',
            group: 'Icons section',
            default: '1-column',
            info: 'Same, but on phones: 1 column stacks each icon-and-text; 2 columns places them two per row.',
            options: [
                { value: '1-column', label: '1 column' },
                { value: '2-columns', label: '2 columns' },
            ],
        },
        // ── Content (Shrine "Content" header) ──────────────────────────────────────
        {
            id: 'desktopContentAlignment',
            type: 'select',
            label: 'Desktop content alignment',
            group: 'Content',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'layout',
            type: 'select',
            label: 'Desktop layout',
            group: 'Content',
            default: 'image_first',
            options: [
                { value: 'text_first', label: 'Content first' },
                { value: 'image_first', label: 'Media first' },
            ],
        },
        {
            id: 'mobileLayout',
            type: 'select',
            label: 'Mobile layout',
            group: 'Content',
            default: 'text_first',
            options: [
                { value: 'text_first', label: 'Content first' },
                { value: 'image_first', label: 'Media first' },
            ],
        },
        {
            id: 'hideContentOnMobile',
            type: 'checkbox',
            label: 'Hide content on mobile',
            group: 'Content',
            default: false,
        },
        // ── Custom colors (Shrine custom_colors_* — revealed by color scheme = "Custom") ──
        // Gated on `colorScheme === 'custom'` (matches Shrine + custom-columns): the pickers show only in the
        // custom scheme, and Component.tsx applies them only then — otherwise the section follows the theme.
        {
            id: 'customColorsBackground',
            type: 'color',
            label: 'Custom background',
            group: 'Custom colors',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsText',
            type: 'color',
            label: 'Custom text',
            group: 'Custom colors',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: 'Custom colors',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonText',
            type: 'color',
            label: 'Custom solid button text',
            group: 'Custom colors',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsOutlineButton',
            type: 'color',
            label: 'Custom outline button color',
            group: 'Custom colors',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared engine — Shrine pattern_* set) ───────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Section (universal padding / width). `colorScheme` is declared locally above so it can carry the
        //    extra `custom` value, so drop the universal one here to avoid a duplicate field id. ─
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields.filter((f) => f.id !== 'colorScheme'),
    ],
    // CHILD BLOCKS — Shrine's full block roster, declared inline.
    blocks: [
        // ── icon — repeatable "Icon with text" list item ─────────────────────────
        {
            type: 'icon',
            name: 'Icon with text',
            fields: [
                {
                    id: 'icon',
                    type: 'icon-name',
                    label: 'Icon',
                    default: 'check_circle',
                    placeholder: 'check_circle',
                    info: 'Type a Google Material Symbols name (e.g. check_circle, local_shipping, verified). Upload a custom image below to override.',
                },
                {
                    id: 'filledIcon',
                    type: 'checkbox',
                    label: 'Filled icon',
                    default: false,
                    info: 'Renders the solid (filled) variant of the icon.',
                },
                { id: 'image', type: 'image', label: 'Custom icon image' },
                {
                    id: 'title',
                    type: 'text',
                    label: 'Heading',
                    default: 'Titre de l’icône',
                    defaultKey: 'section.icon_title',
                    // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
                    aiWritable: true,
                    maxLength: 80,
                },
                {
                    id: 'text',
                    type: 'richtext',
                    label: 'Text',
                    default: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients.</p>',
                    },
                },
            ],
        },
        // ── heading (limit 1) ────────────────────────────────────────────────────
        {
            type: 'heading',
            name: 'Heading',
            max: 1,
            fields: [
                {
                    // Rich text (0019 heading model): Bold a word → it renders in the highlight colour below
                    // (Bold = `<strong>`, which the heading CSS colours). Italic/underline/links also available.
                    // Backward-compatible: an instance saved as a plain string with `**word**` still renders +
                    // opens in the editor (see richHeading / RichtextControl's legacy-string coercion).
                    id: 'title',
                    type: 'richtext',
                    label: 'Heading',
                    default: { format: 'html', html: '<p>Titre de la <strong>section</strong></p>' },
                    info: 'Bold a word to apply the highlight color.',
                },
                {
                    id: 'titleHighlightColor',
                    type: 'color',
                    label: 'Heading highlight color',
                    info: 'The colour applied to bold words in the heading.',
                    default: { ref: 'theme', token: 'accent' },
                },
                {
                    id: 'headingSize',
                    type: 'select',
                    label: 'Heading size',
                    default: 'h2',
                    options: [
                        { value: 'h2', label: 'Small' },
                        { value: 'h1', label: 'Medium' },
                        { value: 'h0', label: 'Large' },
                    ],
                },
            ],
        },
        // ── caption (limit 1) ────────────────────────────────────────────────────
        {
            type: 'caption',
            name: 'Caption',
            max: 1,
            fields: [
                { id: 'caption', type: 'text', label: 'Caption', default: 'Sous-titre', maxLength: 80 },
                {
                    id: 'textStyle',
                    type: 'select',
                    label: 'Caption style',
                    default: 'caption-with-letter-spacing',
                    options: [
                        { value: 'subtitle', label: 'Subtitle' },
                        { value: 'caption-with-letter-spacing', label: 'Uppercase' },
                    ],
                },
                {
                    id: 'textSize',
                    type: 'select',
                    label: 'Caption size',
                    default: 'medium',
                    options: [
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                    ],
                },
            ],
        },
        // ── text (limit 1) ───────────────────────────────────────────────────────
        {
            type: 'text',
            name: 'Text',
            max: 1,
            fields: [
                {
                    id: 'text',
                    type: 'richtext',
                    label: 'Text',
                    default: {
                        format: 'html',
                        html: '<p>Décrivez ici votre marque, un avantage clé ou une promesse qui rassure vos clients et les incite à passer commande.</p>',
                    },
                },
                {
                    id: 'textStyle',
                    type: 'select',
                    label: 'Text style',
                    default: 'body',
                    options: [
                        { value: 'body', label: 'Body' },
                        { value: 'subtitle', label: 'Subtitle' },
                    ],
                },
            ],
        },
        // ── button (limit 1) ─────────────────────────────────────────────────────
        {
            type: 'button',
            name: 'Button',
            max: 1,
            fields: [
                {
                    id: 'buttonLabel',
                    type: 'text',
                    label: 'Button label',
                    default: 'En savoir plus',
                    defaultKey: 'common.learn_more',
                    maxLength: 40,
                },
                { id: 'buttonLink', type: 'link', label: 'Button link' },
                {
                    id: 'buttonStyleSecondary',
                    type: 'checkbox',
                    label: 'Use outline button style',
                    default: false,
                },
            ],
        },
        // ── atc_button (limit 1) — reads the image block's atc_product / atc_skip_cart ──
        {
            type: 'atc_button',
            name: 'Add to Cart button',
            max: 1,
            fields: [
                {
                    id: 'buttonLabel',
                    type: 'text',
                    label: 'Button label',
                    default: 'Ajouter au panier',
                    defaultKey: 'product.add_to_cart',
                    maxLength: 40,
                    info: 'Targets the product picked on the Image block (atc product), like Shrine.',
                },
            ],
        },
        // ── image (limit 1) — media + the atc inputs Shrine physically stores here ──
        {
            type: 'image',
            name: 'Image',
            max: 1,
            fields: [
                { id: 'image', type: 'image', label: 'Image' },
                {
                    id: 'atcProduct',
                    type: 'product-picker',
                    label: 'Add to Cart product',
                    info: 'Used by the Add to Cart button block.',
                },
                {
                    id: 'atcSkipCart',
                    type: 'checkbox',
                    label: 'Skip cart (go to checkout)',
                },
            ],
        },
        // ── video (limit 1) — autoplay hosted video ──────────────────────────────
        {
            type: 'video',
            name: 'Autoplay video',
            max: 1,
            fields: [
                {
                    id: 'video',
                    type: 'text',
                    label: 'Video URL',
                    info: 'Hosted MP4/WebM URL (the platform video resource picker is deferred).',
                },
                {
                    id: 'videoMutedAutoplay',
                    type: 'checkbox',
                    label: 'Muted autoplay',
                    default: true,
                },
                playButtonField({
                    defaultOn: false,
                    visibleWhen: [
                        { field: 'video', notEquals: '' },
                        { field: 'videoMutedAutoplay', equals: true },
                    ],
                }),
            ],
        },
    ],
    migrate(fromVersion, settings) {
        if (fromVersion >= 2)
            return settings;
        // v1 → v2: the v1 section carried section-level heading/body/columns/iconSize(small|medium|large)/
        // iconPosition(above|inline)/contentAlignment. The heading/body now live in `heading`+`text` blocks
        // (added by hydration via the preset), so we only remap the surviving scalar settings here.
        const next = { ...settings };
        const sizeMap = { small: 's', medium: 'm', large: 'l' };
        if (typeof next.iconSize === 'string' && sizeMap[next.iconSize]) {
            next.iconSize = sizeMap[next.iconSize];
        }
        if (next.iconPosition === 'inline')
            next.iconPosition = 'next-to-text';
        if (typeof next.contentAlignment === 'string') {
            next.iconTextAlignment = next.contentAlignment;
        }
        const colsMap = { '2': '2-columns', '3': '1-column', '4': '2-columns' };
        if (typeof next.columns === 'string' && colsMap[next.columns]) {
            next.iconsDesktopLayout = colsMap[next.columns];
        }
        return next;
    },
};
export const iconsWithContentPresets = [
    // Shrine default preset: 6 blocks in order — icon, icon, icon, heading, text, button.
    {
        name: 'Icons with content',
        settings: {
            visibility: 'always-display',
            iconSize: 's',
            iconPosition: 'next-to-title',
            iconColor: 'custom',
            iconCustomColor: { ref: 'theme', token: 'ink' },
            iconHeadingSize: 'h3',
            iconTextAlignment: 'left',
            iconsDesktopLayout: '1-column',
            iconsMobileLayout: '1-column',
            desktopContentAlignment: 'left',
            layout: 'image_first',
            mobileLayout: 'text_first',
            hideContentOnMobile: false,
            customColorsBackground: { ref: 'custom', value: '#FFFFFF' },
            customColorsText: { ref: 'custom', value: '#2E2A39' },
            customColorsSolidButtonBackground: { ref: 'custom', value: '#dd1d1d' },
            customColorsSolidButtonText: { ref: 'custom', value: '#ffffff' },
            customColorsOutlineButton: { ref: 'custom', value: '#dd1d1d' },
            patternBg: 'none',
            colorScheme: 'light',
            paddingTop: 36,
            paddingBottom: 36,
            width: 'standard',
        },
        // NEUTRAL PLACEHOLDER template (same convention as Multicolumn): three identical, obviously-editable
        // icon rows + a generic heading/text/button — NOT a pre-filled marketing demo (no "Livraison rapide"
        // etc.), NOT lorem ipsum. The media slot keeps a demo image so the layout reads complete, like
        // image-with-text; the seller replaces copy + image with their own.
        blocks: [
            {
                type: 'icon',
                settings: {
                    icon: 'check_circle',
                    filledIcon: false,
                    text: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients.</p>',
                    },
                },
            },
            {
                type: 'icon',
                settings: {
                    icon: 'check_circle',
                    filledIcon: false,
                    text: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients.</p>',
                    },
                },
            },
            {
                type: 'icon',
                settings: {
                    icon: 'check_circle',
                    filledIcon: false,
                    text: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients.</p>',
                    },
                },
            },
            {
                type: 'heading',
                settings: {
                    title: { format: 'html', html: '<p>Titre de la <strong>section</strong></p>' },
                    titleHighlightColor: { ref: 'theme', token: 'accent' },
                    headingSize: 'h2',
                },
            },
            {
                type: 'text',
                settings: {
                    text: {
                        format: 'html',
                        html: '<p>Décrivez ici votre marque, un avantage clé ou une promesse qui rassure vos clients et les incite à passer commande.</p>',
                    },
                    textStyle: 'body',
                },
            },
            {
                type: 'button',
                settings: {
                    buttonLink: { type: 'external', url: '', label: 'En savoir plus' },
                    buttonStyleSecondary: false,
                },
            },
            {
                // Bloc média VIDE (direction fondatrice 2026-09-16) : la colonne affiche le placeholder scénique
                // jusqu'à ce que la vendeuse dépose sa photo, au lieu d'une photo maison à chasser après coup.
                type: 'image',
                settings: {},
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const iconsWithContentMeta = {
    label: 'Icons with content',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map